import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveIcon from '@mui/icons-material/Remove';
import WarningIcon from '@mui/icons-material/Warning';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import type { Theme } from '@mui/material/styles';
import type { AccountBaselineView } from '../../../types/backend';
import {
  deriveOnboardingGates,
  summariseGates,
  type GateState,
  type OnboardingGate,
  type RegionTarget,
} from '../deriveOnboardingGates';

function stateColor(state: GateState, theme: Theme): string {
  switch (state) {
    case 'done': return theme.palette.success.main;
    case 'running': return theme.palette.info.main;
    // A halted step needs a person, which is not the same as a fault. Red is
    // kept for something that has actually gone wrong.
    case 'failed': return theme.palette.warning.main;
    case 'unwanted': return theme.palette.warning.main;
    // Deliberately not the error colour: we did not get an answer, and a red
    // mark here sends someone to investigate a thing that may be fine.
    case 'inconclusive': return theme.palette.text.secondary;
    case 'pending': return theme.palette.text.secondary;
    default: return theme.palette.text.disabled;
  }
}

const GateIcon: React.FC<{ state: GateState }> = ({ state }) => {
  const sx = { fontSize: 18, color: (t: Theme) => stateColor(state, t) };
  if (state === 'done') return <CheckCircleIcon sx={sx} />;
  if (state === 'running') return <SyncIcon sx={sx} />;
  if (state === 'failed') return <ErrorIcon sx={sx} />;
  if (state === 'unwanted') return <WarningIcon sx={sx} />;
  if (state === 'inconclusive') return <HelpOutlineIcon sx={sx} />;
  // A gate nothing observes gets a dash, not an empty circle: an empty circle
  // is a thing that has not happened yet, and these are things nobody checks.
  if (state === 'unobserved') return <RemoveIcon sx={sx} />;
  return <RadioButtonUncheckedIcon sx={sx} />;
};

/**
 * A region is a deployment, not a label, so it carries its own state. One
 * region converged out of three is one done and two waiting, and only the
 * pill says which one is stuck.
 *
 * An unwanted region keeps its convergence dot and gains a "not wanted" tag,
 * rather than trading one fact for the other: how settled the stack is says
 * how hard removing it will be.
 */
const RegionPill: React.FC<{ target: RegionTarget }> = ({ target }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      pl: 0.75,
      pr: 1,
      py: 0.25,
      borderRadius: 1,
      border: 1,
      borderStyle: target.wanted ? 'solid' : 'dashed',
      borderColor: (t) =>
        !target.wanted
          ? t.palette.warning.main
          : target.state === 'pending'
            ? t.palette.divider
            : stateColor(target.state, t),
    }}
  >
    <Box
      aria-hidden
      sx={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        bgcolor: (t) => stateColor(target.state, t),
        opacity: target.state === 'pending' ? 0.5 : 1,
      }}
    />
    <Typography variant="caption" color={target.state === 'pending' ? 'text.secondary' : 'text.primary'}>
      {target.region}
    </Typography>
    {target.isMain && (
      <Typography variant="caption" color="text.disabled">main</Typography>
    )}
    {!target.wanted && (
      <Typography variant="caption" color="warning.main">not wanted</Typography>
    )}
  </Box>
);

const GateRow: React.FC<{ gate: OnboardingGate }> = ({ gate }) => {
  // A satisfied gate needs one line. Everything else is the reason it is not.
  const showDetail = gate.state !== 'done';

  return (
    <Box
      role="listitem"
      sx={{
        display: 'grid',
        gridTemplateColumns: '18px minmax(0, 1fr)',
        columnGap: 1.5,
        rowGap: 0.5,
        py: 1.25,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ pt: '2px' }}><GateIcon state={gate.state} /></Box>

      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: gate.state === 'unobserved' ? 'text.secondary' : 'text.primary' }}
          >
            {gate.title}
          </Typography>
          {gate.owner && gate.state !== 'done' && (
            <Typography variant="caption" color="text.disabled">{gate.owner}</Typography>
          )}
        </Box>

        {(gate.reason || showDetail) && (
          <Typography
            variant="caption"
            color={gate.reason ? 'warning.main' : 'text.secondary'}
            sx={{ display: 'block', wordBreak: 'break-word' }}
          >
            {gate.reason || gate.detail}
          </Typography>
        )}

        {gate.targets.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
            {gate.targets.map((target) => <RegionPill key={target.region} target={target} />)}
          </Box>
        )}
      </Box>
    </Box>
  );
};

/** Where this account is in onboarding, and what is holding it. */
const OnboardingGates: React.FC<{ account: AccountBaselineView; enabled?: boolean }> = ({
  account,
  enabled = true,
}) => {
  const gates = deriveOnboardingGates(account);
  const { done, total, uncheckable, blocked } = summariseGates(gates);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        {/*
          A disabled account is skipped entirely, so every unfinished step
          below is waiting on this rather than on AWS. Saying so stops the
          list reading as work in progress.
        */}
        {!enabled && (
          <Box
            sx={{
              mb: 1.5,
              px: 1.5,
              py: 1,
              border: 1,
              borderColor: 'warning.main',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
              This account is inactive
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Nothing below will run until it is made active
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1, mb: 0.75 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {!enabled
              ? `${done} of ${total} steps done, and paused`
              : blocked
                ? `Blocked at ${blocked.title.toLowerCase()}`
                : `${done} of ${total} steps done`}
          </Typography>
          {uncheckable > 0 && (
            // Said out loud so a low number reads as "we cannot see most of
            // this" rather than as an account in trouble.
            <Typography variant="caption" color="text.disabled">
              {uncheckable} cannot be checked
            </Typography>
          )}
        </Box>
        <LinearProgress
          variant="determinate"
          value={total === 0 ? 0 : (done / total) * 100}
          color={blocked ? 'error' : 'success'}
          sx={{ height: 4, borderRadius: 2 }}
        />
      </Box>

      <Box role="list" aria-label="Onboarding steps">
        {gates.map((gate) => <GateRow key={gate.key} gate={gate} />)}
      </Box>
    </Box>
  );
};

export default OnboardingGates;
